/**
 * Estimate how long an SMS template is once sent, and how many SMS (segments)
 * it costs. Mirrors the backend's utils/smsSegments.js counting:
 * GSM text is 160 chars for 1 SMS, 153 per SMS when split;
 * any non-GSM character (e.g. "—", emoji, Sinhala/Tamil) switches to 70 / 67.
 */

const GSM_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ ÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXTENDED = "^{}\\[~]|€";

/** Typical values used in place of placeholders when estimating length. */
export const SMS_SAMPLE_VALUES: Record<string, string> = {
  patient_name: "Kamal Perera",
  patient_title: "Mr.",
  doctor_name: "Dr. Nimal Silva",
  clinic_name: "Smile Dental Clinic",
  appointment_date: "2026-10-05",
  appointment_time: "10:30 AM",
  token_number: "12",
  registration_number: "REG-00123",
  invoice_link: "https://app.multidental.lk/invoice/6720a1b2c3d4e5f601234567",
  treatment_name: "Root canal treatment",
  holiday_name: "Sinhala & Tamil New Year",
};

export type SmsLengthEstimate = {
  /** Characters the patient receives (line break = 1). */
  length: number;
  segments: number;
  /** True when a non-GSM character forces the 70-char Unicode limit. */
  unicode: boolean;
  /** Characters allowed in a single SMS for this encoding. */
  singleLimit: number;
};

/**
 * Turn a template into roughly what the patient receives: placeholders filled
 * with sample values and typed "\n" treated as a line break (Notify.lk sends
 * both the Enter key and a typed \n as one new line).
 */
export function renderSmsSample(
  template: string,
  sampleValues: Record<string, string> = SMS_SAMPLE_VALUES
): string {
  return String(template || "")
    .replace(/\r\n?/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/{{(.*?)}}/g, (_, key) => {
      const lookup = String(key || "").trim().toLowerCase();
      return sampleValues[lookup] ?? "";
    });
}

function isGsmChar(ch: string): boolean {
  return GSM_BASIC.includes(ch) || GSM_EXTENDED.includes(ch);
}

export function estimateSmsLength(text: string): SmsLengthEstimate {
  const raw = String(text || "");
  const chars = Array.from(raw);
  const unicode = chars.some((ch) => !isGsmChar(ch));

  if (!unicode) {
    const length = chars.reduce((sum, ch) => sum + (GSM_EXTENDED.includes(ch) ? 2 : 1), 0);
    const segments = length === 0 ? 0 : length <= 160 ? 1 : Math.ceil(length / 153);
    return { length, segments, unicode, singleLimit: 160 };
  }

  const length = chars.length;
  const segments = length <= 70 ? 1 : Math.ceil(length / 67);
  return { length, segments, unicode, singleLimit: 70 };
}

export function estimateSmsTemplate(template: string): SmsLengthEstimate {
  return estimateSmsLength(renderSmsSample(template));
}

/** First character that forces Unicode encoding, for the warning text. */
export function firstUnicodeChar(text: string): string | null {
  for (const ch of Array.from(renderSmsSample(text))) {
    if (!isGsmChar(ch)) return ch;
  }
  return null;
}
